// Recherche et tri des tutos (D-082) : sans accents ni majuscules, tous les mots doivent
// se trouver (titre, mots-clés, texte) ; le titre et les mots-clés passent devant.
import { THEMES, type HelpArticle, type HelpRole, type HelpTheme } from './articles';

/** « Écrire l’e-mail » → « ecrire l email » : minuscules, sans accents, apostrophes et tirets neutres. */
export function fold(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
    .replace(/œ/g, 'oe').replace(/æ/g, 'ae')
    .replace(/(\w)[-‐‑](\w)/g, '$1$2')          // e-mail = email, rendez-vous = rendezvous
    .replace(/[’'`«»"“”.,;:!?()/…›‹→·]/g, ' ')
    .replace(/\s+/g, ' ').trim();
}

/** Les mots d'une recherche, pluriels ramenés au singulier (« absences » → « absence »). */
export function terms(query: string): string[] {
  return fold(query).split(' ').filter(w => w.length > 0).map(w => (w.length > 3 && /[sx]$/.test(w) ? w.slice(0, -1) : w));
}

const textOf = (a: HelpArticle) => fold([a.intro, ...a.steps, ...(a.tips ?? [])].join(' '));

/** Les tutos qui contiennent tous les mots, les plus pertinents d'abord (ordre d'origine à égalité). */
export function searchArticles(list: HelpArticle[], query: string): HelpArticle[] {
  const ws = terms(query);
  if (!ws.length) return list;
  const scored = list.map((a, i) => {
    const title = fold(a.title), keys = fold(a.keywords.join(' ')), body = textOf(a);
    let score = 0;
    for (const w of ws) {
      if (title.includes(w)) score += 3;
      else if (keys.includes(w)) score += 2;
      else if (body.includes(w)) score += 1;
      else return { a, i, score: -1 };
    }
    return { a, i, score };
  });
  return scored.filter(x => x.score > 0).sort((x, y) => y.score - x.score || x.i - y.i).map(x => x.a);
}

/** Les tutos qui concernent l'un de ces rôles (« tous » : tout le monde). */
export const forRoles = (list: HelpArticle[], roles: ReadonlySet<HelpRole>): HelpArticle[] =>
  list.filter(a => a.roles.some(r => r === 'tous' || roles.has(r)));

/** Par thème, dans l'ordre des thèmes ; les thèmes vides sont omis. */
export function byTheme(list: HelpArticle[]): { theme: HelpTheme; label: string; icon: string; items: HelpArticle[] }[] {
  return THEMES.map(t => ({ theme: t.key, label: t.label, icon: t.icon, items: list.filter(a => a.theme === t.key) })).filter(g => g.items.length);
}

/** Une étape découpée en texte et en libellés d'écran (« … »), pour les mettre en valeur. */
export function stepParts(step: string): { text: string; ui: boolean }[] {
  const out: { text: string; ui: boolean }[] = [];
  const re = /«\s?([^»]+?)\s?»/g;
  let last = 0, m: RegExpExecArray | null;
  while ((m = re.exec(step))) {
    if (m.index > last) out.push({ text: step.slice(last, m.index), ui: false });
    out.push({ text: m[1]!, ui: true });
    last = m.index + m[0].length;
  }
  if (last < step.length) out.push({ text: step.slice(last), ui: false });
  return out;
}
